/**
 * SUPERVISOR-ACCOUNTS-LIST
 * List of IDTree accounts (with search, pages and free trials)
 */

import { useEffect, useState } from "react"
import { connect } from "react-redux"
import { WithTranslation, withTranslation } from "react-i18next"
import Card from "@/components/card"
import ListContainer from "@/components/list-container"
import { Account, AccountState } from "@/redux/account.types"
import {
  accountStatus,
  accountFetch_AsSupervisor,
  accountFetchCount_AsSupervisor,
  accountGetCount,
  accountGetCountSearch,
  accountGet
} from "@/redux/account.actions"
import { store } from "@/index"
import { STATUS_LOADED, STATUS_LOADING, STATUS_LOAD_ERROR, STATUS_SEARCHING } from "@/redux/_status.types"
import ListItem from "@/components/list-item"
import ListFields from "@/components/list-fields"
import Chip from "@/components/chip"
import ListCell from "@/components/list-cell"
import Space from "@/components/space"
import { faPencilAlt, faPlayCircle, faShieldAlt } from "@fortawesome/free-solid-svg-icons"
import ListButton from "@/components/list-button"
import ListIcon from "@/components/list-icon"
import PageLoader from "@/components/page-loader"
import ModulesWidget from "@/widgets/modules.widget"
import Checkbox from "@/components/checkbox"
import { formatDate } from "@/utils/format-date.utils"
import { getImageUrl } from "@/utils/get-image-url"

interface StateProps extends WithTranslation {
  account: AccountState
}

interface OwnProps {
  onClick: (account: Account) => void //Clic on an account
  onEdit?: (account: Account) => void //Clic on edit (show edit and connect buttons)
  isWithoutCard?: boolean //Display without the card style (in a modal)
  salesId?: string | null //Only the accounts of this AE
}

type Props = StateProps & OwnProps

//Limit of account displayed by page
const LIMIT: number = 5

function SupervisorAccountsListWidget(props: Props) {
  const { t } = props

  const [currentOffset, setCurrentOffset] = useState<number>(0)
  const [currentSearch, setCurrentSearch] = useState<string>("")
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [showTrials, setShowTrials] = useState(false)

  //Load account on start
  useEffect(() => {
    loadAccounts()

    //Load account
    async function loadAccounts() {
      store.dispatch(accountStatus(STATUS_SEARCHING))

      //Do query (get list and count)
      const accounts: any = await store.dispatch(
        accountFetch_AsSupervisor(currentSearch, currentOffset, LIMIT, showTrials, props.salesId)
      )
      const accountsCount: any = await store.dispatch(
        accountFetchCount_AsSupervisor(currentSearch, showTrials, props.salesId)
      )
      setIsLoaded(true)

      if (accounts.error || accountsCount.error) {
        store.dispatch(accountStatus(STATUS_LOAD_ERROR))
      } else {
        //Update list
        store.dispatch(accountStatus(STATUS_LOADED))
        store.dispatch(accountGet(accounts))

        //Update store
        store.dispatch(accountGetCountSearch(accountsCount.count))
        if (!currentSearch.length) store.dispatch(accountGetCount(accountsCount.count))
      }
    }
  }, [currentOffset, currentSearch, showTrials])

  //Search account
  function searchAccounts(search: string) {
    setCurrentSearch(search)
    setCurrentOffset(0)
    setIsLoaded(false)
  }

  //Change page
  function setPage(offset: number) {
    setCurrentOffset(offset)
    setIsLoaded(false)
  }

  return (
    <Card isWithoutPadding isOverflowHidden isNotCardClass={props.isWithoutCard} className="rel">
      {/* After the search bar and its clear button */}
      <div style={{ position: "absolute", top: 23, left: 430 }}>
        <Checkbox
          active={showTrials}
          text="Afficher les essais gratuits"
          onClick={() => setShowTrials(!showTrials)}></Checkbox>
      </div>
      <ListContainer
        countLimit={LIMIT}
        countSearch={props.account.countSearch}
        isFooterActive
        onSearch={searchAccounts}
        onSearchStart={() => store.dispatch(accountStatus(STATUS_SEARCHING))}
        onSetPage={setPage}
        status={props.account.status}>
        <PageLoader status={isLoaded ? STATUS_LOADED : STATUS_LOADING}>
          <div>
            <ListFields>
              <ListCell />
              <ListCell width={200} text={t("user_lastname")} />
              <ListCell width={260} text={t("project")} />
              <Space />
              <ListCell width={300} text="Connexion" />
              <ListCell width={180} text={t("account_modules")} />
              <ListCell />
              <ListCell />
            </ListFields>

            {props.account.list.map((account: Account) => (
              <ListItem isEditable onClick={() => props.onClick(account)} key={account.id}>
                <ListIcon
                  fallbackIcon={faShieldAlt}
                  iconColor={account.colors.brandPrimary}
                  image={account.options.image ? getImageUrl("Accounts", account.id) : null}
                />

                <ListCell width={200} text={account.name} />

                <div style={{ width: 260 }}>
                  <div className="flex">
                    {!account.options.isDemoAccount && account.Projects[0] ? (
                      <Chip color={account.Projects[0].statusColor}>{account.projectStatus}</Chip>
                    ) : (
                      !account.options.isDemoAccount && account.projectStatus
                    )}
                  </div>

                  {!account.options.isDemoAccount && account.options.onboardingAction && (
                    <div
                      className="grey-t"
                      style={{
                        fontSize: 12,
                        marginTop: account.projectStatus ? 8 : 0
                      }}>
                      Action : <b>{t("home_welcome_" + account.options.onboardingAction)}</b>
                    </div>
                  )}
                </div>

                <Space />

                {!account.options.isDemoAccount && (
                  <ListCell width={300}>
                    <div style={{ fontSize: 12 }}>
                      <div>
                        Création du compte : <b>{account.dateStartLabel}</b>
                      </div>
                      {account.Users[0]?.connectedAt && (
                        <div>
                          Dernière connexion : <b>{formatDate(account.Users[0].connectedAt, true, false)}</b>
                        </div>
                      )}
                    </div>
                  </ListCell>
                )}

                {account.options.isDemoAccount && <ListCell width={300} />}

                {!account.options.isDemoAccount && (
                  <ListCell width={180}>
                    <ModulesWidget features={account.features} modules={account.modules} readonly />
                  </ListCell>
                )}

                {account.options.isDemoAccount && <ListCell width={180} />}

                {props.onEdit && (
                  <>
                    <ListButton icon={faPencilAlt} onClick={() => props.onEdit?.(account)} text={t("account_edit")} />

                    <ListButton
                      icon={faPlayCircle}
                      onClick={() => props.onClick(account)}
                      isPrimary
                      text={t("utils_connect")}
                    />
                  </>
                )}
              </ListItem>
            ))}
          </div>
        </PageLoader>
      </ListContainer>
    </Card>
  )
}

const mapStateToProps = (state) => ({
  account: state.account
})

export default connect(mapStateToProps)(withTranslation()(SupervisorAccountsListWidget))
